import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  computed,
  forwardRef,
  signal,
  effect,
} from '@angular/core';

// Unique ID counter for select components
let selectIdCounter = 0;
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface SelectOption {
  label: string;
  value: any;
  disabled?: boolean;
}

export type SelectSize = 'sm' | 'md' | 'lg';

@Component({
  selector: 'app-atom-select',
  standalone: true,
  imports: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true,
    },
  ],
})
export class SelectComponent implements ControlValueAccessor {
  // Generate unique ID for this component instance
  private readonly uniqueId = `select-${++selectIdCounter}`;

  // Signal-based inputs
  options = input<SelectOption[]>([]);
  placeholder = input<string>('Select an option');
  disabledInput = input(false, { alias: 'disabled' });
  private _disabledFromCVA = signal<boolean>(false);
  disabled = computed(() => this.disabledInput() || this._disabledFromCVA());
  required = input<boolean>(false);
  error = input<boolean>(false);
  hasError = input<boolean>(false);
  errorMessage = input<string>('');
  label = input<string>('');
  helperText = input<string>('');
  id = input<string>('');
  name = input<string>('');
  // Value input for standalone usage (without forms)
  value = input<any>(null);

  // Computed ID that falls back to unique ID if not provided
  readonly selectId = computed(() => this.id() || this.uniqueId);
  multiple = input<boolean>(false);
  size = input<SelectSize>('md');
  additionalClasses = input<string>('');

  // Signal-based output
  valueChange = output<any>();

  // Internal value signal for ControlValueAccessor
  internalValue = signal<any>(null);

  // ControlValueAccessor callbacks
  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    // Sync value input with internalValue for standalone usage
    effect(
      () => {
        const val = this.value();
        if (val !== null && val !== undefined) {
          this.internalValue.set(val);
        }
      },
      { allowSignalWrites: true }
    );
  }

  // Computed classes for select element
  selectClasses = computed(() => {
    const classes: string[] = [
      'w-full',
      'border-2',
      'rounded-lg',
      'transition-all',
      'duration-200',
      'focus:outline-none',
      'focus:ring-2',
      'focus:ring-offset-0',
      'appearance-none',
      'bg-no-repeat',
      'bg-right',
      'pr-10',
    ];

    // Size classes
    const sizeMap: Record<SelectSize, string> = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-4 py-2.5 text-sm',
      lg: 'px-4 py-3 text-base',
    };
    classes.push(sizeMap[this.size()]);

    // Error state
    if (this.error() || this.hasError()) {
      classes.push(
        'border-danger-500',
        'focus:ring-danger-500/20',
        'focus:border-danger-500',
        'bg-red-50/50'
      );
    } else {
      classes.push(
        'border-gray-200',
        'focus:ring-primary-500/20',
        'focus:border-primary-500',
        'bg-white',
        'hover:border-gray-300'
      );
    }

    // Disabled state
    if (this.disabled()) {
      classes.push('!bg-gray-100', 'cursor-not-allowed', 'opacity-60');
    } else {
      classes.push('cursor-pointer');
    }

    if (this.additionalClasses()) {
      classes.push(this.additionalClasses());
    }

    return classes.join(' ');
  });

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    this.internalValue.set(value);
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    // Handled via input signal
  }

  onSelectionChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const newValue = this.multiple()
      ? Array.from(target.selectedOptions).map((opt: HTMLOptionElement) => opt.value)
      : target.value;

    this.internalValue.set(newValue);
    this.onChange(newValue);
    this.valueChange.emit(newValue);
  }

  onBlur(): void {
    this.onTouched();
  }
}
